import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl, AbstractControlOptions, AbstractControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { MatStepper } from '@angular/material/stepper';
import { ActivatedRoute } from '@angular/router';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { Observable, map, startWith } from 'rxjs';
import { UserOnboarding } from 'src/app/core/models/user/user-onboarding.model';
import { NospaceValidator } from 'src/app/core/validators/nospace-validator';
import { OutsideClickErrorStateMatcher } from 'src/app/core/validators/outclick-validators';
import { MustMatch } from 'src/app/core/validators/password-validator';
import { LocationDTO } from 'src/app/domain/location/location.dto';
import { AuthFacadeService } from 'src/app/facades/auth/auth-facade.service';
import { LocationFacadesService } from 'src/app/facades/location/location-facades.service';
import { StylistFacadesService } from 'src/app/facades/stylist/stylist-facades.service';
import { UserFacadeService } from 'src/app/facades/user/user-facade.service';
import { FeedbackSnackbarService } from 'src/app/core/utilities/feedback-snackbar/feedback-snackbar.service';

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss']
})
export class OnboardingComponent implements OnInit {
  @ViewChild('stepper') stepper: MatStepper;
  isSubmitted: boolean = false;
  showNewPassword!: boolean;
  showConfirmPassword!: boolean;
  locationFormGroup: FormGroup;
  emailFormGroup: FormGroup;
  personalFormGroup: FormGroup;
  loftInfoFormGroup: FormGroup;
  previewFormGroup: FormGroup;
  successDisplay: boolean = false;
  isProcessing: boolean = false;
  enableSave = false;
  locations: any[] = [];
  filteredLocations: Observable<LocationDTO[]>;
  formSubmissionInfo: any;
  signupAuthInfo: any;
  matcher = new OutsideClickErrorStateMatcher();
  phoneInputComponent: NgxMatIntlTelInputComponent;
  passwordMessage: string = "";
  hidePasswords: boolean = false;
  isLogOn = false;
  onBoardingUserDetails: UserOnboarding;
  stopRegistration: boolean = false;
  loadingUserDataByEmail: boolean = false;
  completeRegistration: boolean = false;
  emailError: boolean = false;
  clientId: any;
  @ViewChild('phoneInput', { static: false }) set phoneInput(
    content: NgxMatIntlTelInputComponent) {
    if (content) {
      this.phoneInputComponent = content;
    }
  }
  selectedCountry: any = { iso2: 'us', dialCode: '+1' };
  // personalFormOptions: AbstractControlOptions = {
  //   validators: MustMatch('passwordCtrl', 'confirmPassCtrl')
  // }

  constructor(
    private formBuilder: FormBuilder,
    private locationFacadeService: LocationFacadesService,
    private authServiceFacade: AuthFacadeService,
    private userFacadeService: UserFacadeService,
    private stylistFacadeService: StylistFacadesService,
    private activatedRouter: ActivatedRoute,
    private snackBar: FeedbackSnackbarService
  ) {
    this.clientId = this.activatedRouter.snapshot.queryParamMap.get('ClientId');
    this.locationFormGroup = this.formBuilder.group({
      "locationCtrl": new FormControl('', Validators.required),
      "locationIdCtrl": new FormControl('', Validators.required)
    });
    this.previewFormGroup = this.formBuilder.group({
      "locationCtrl": new FormControl(''),
      "fnameCtrl": new FormControl(''),
      "lnameCtrl": new FormControl(''),
      "emailCtrl": new FormControl(''),
      "mobileCtrl": new FormControl(''),
      "passwordCtrl": new FormControl(''),
      "loftNoCtrl": new FormControl(''),
      "loftNameCtrl": new FormControl('')
    })
    this.personalFormGroup = this.formBuilder.group({
      "fnameCtrl": new FormControl('', [Validators.required, NospaceValidator.blockSpace]),
      "lnameCtrl": new FormControl('', [Validators.required, NospaceValidator.blockSpace]),
      "mobileCtrl": new FormControl('', [Validators.required]),
      "passwordCtrl": new FormControl('', [Validators.required, Validators.minLength(8), Validators.pattern('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$')]),
      "confirmPassCtrl": new FormControl('', [Validators.required])
    },{
      validator: MustMatch('password', 'confirmPassword')
    });
    this.emailFormGroup = this.formBuilder.group({
      "emailCtrl": new FormControl('', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,4}$'), NospaceValidator.blockSpace]),
    });
    this.loftInfoFormGroup = this.formBuilder.group({
      "loftNoCtrl": new FormControl('', [Validators.required, NospaceValidator.blockSpace]),
      "loftNameCtrl": new FormControl('', [Validators.required, NospaceValidator.blockSpace])
    });
  }

  ngOnInit(): void {
    this.isProcessing = true;
    this.fetchLocationsList();
  }

  get passwordCtrl(): AbstractControl {
    return this.personalFormGroup.get('passwordCtrl');
  }

  get confirmPassCtrl(): AbstractControl {
    return this.personalFormGroup.get('confirmPassCtrl');
  }

  fetchLocationsList(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const clientId = this.clientId? this.clientId : 73;
      this.locationFacadeService.getLocationList(clientId).subscribe({
        next: (response: any) => {
          this.locations = response;
          this.isProcessing = false;
          this.registerLocationFilter();
          resolve();
        },
        error: (error) => {
          this.isProcessing = false;
          resolve();
        }
      })
    });
  }

  
  onStepperSelectionChange(event: StepperSelectionEvent) {
    this.previewData();
  }
  
  // Function to format the phone number
  formatPhoneNumber(phoneNumber: string): string {
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');
    // Check if the input is of correct length
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ')-' + match[2] + '-' + match[3];
    }
    return phoneNumber;
  }

  previewData() {
    this.previewFormGroup.patchValue({
      "locationCtrl": this.locationFormGroup.value.locationCtrl,
      "fnameCtrl": this.personalFormGroup.value.fnameCtrl,
      "lnameCtrl": this.personalFormGroup.value.lnameCtrl,
      "emailCtrl": this.emailFormGroup.value.emailCtrl,
      "mobileCtrl": this.formatPhoneNumber(this.phoneInputComponent.phoneNumber),
      "passwordCtrl": this.personalFormGroup.value.passwordCtrl,
      "loftNoCtrl": this.loftInfoFormGroup.value.loftNoCtrl,
      "loftNameCtrl": this.loftInfoFormGroup.value.loftNameCtrl
    });
  }

  editForm() {
    this.stepper.selectedIndex = 0;
  }

  submitOnboardingForm() {
    this.loadingUserDataByEmail = true;
    if (this.locationFormGroup.valid &&
      this.emailFormGroup.valid &&
      this.personalFormGroup.valid &&
      this.loftInfoFormGroup.valid) {
      const formValue = {
        location: this.locationFormGroup.value,
        email: this.emailFormGroup.value,
        personal: this.personalFormGroup.value,
        loft: this.loftInfoFormGroup.value
      };
      this.formSubmissionInfo = formValue;
      const _email = formValue.email.emailCtrl;
      const _pass = formValue.personal.passwordCtrl;
      if (this.onBoardingUserDetails.userType == "Customer" || this.onBoardingUserDetails.userType == "") {
        if(this.onBoardingUserDetails.isFirebaseRegistered == false){
          this.authServiceFacade.signUp(_email, _pass).then((response: any) => {
            const authInfo = {
              authId: response.uid,
              signInMethod: response.providerData[0].providerId
            }
            this.signupAuthInfo = authInfo;
            this.verifyEmail(authInfo);
          }).catch((error) => {
            // handle error here
            this.isProcessing = false;
            this.stopRegistration = true;
            this.errorSocialMessage(error);
          })
        }else{
          this.submitOnboardingDetails();
        }
      } else {
        this.submitOnboardingDetails();
      }

    }
  }

  errorSocialMessage(e: any) {
    let errorMsg: string = e;
    if (e == "email not match") {
      errorMsg = "Please use social media account having same email or create a new password.";
      this.passwordMessage = errorMsg;
    } else {
      if (e.code == 'auth/user-not-found') {
        errorMsg = 'Please enter the valid email address';
        this.passwordMessage = errorMsg;
      } else if (e.code == 'auth/wrong-password' || e.code == 'auth/invalid-password') {
        errorMsg = 'Password is incorrect';
        this.passwordMessage = errorMsg;
      }
      else if (e.code == 'auth/email-already-in-use' || e.code == "auth/email-already-exists") {
        errorMsg = 'This email is already in use';
        this.passwordMessage = errorMsg;
      } else {
        errorMsg = "Something went wrong, Please try again";
        this.passwordMessage = errorMsg;
      }
    }
    this.snackBar.errorSnackbar(errorMsg);
  }

  onTermsChoosen(event: any) {
    this.isLogOn = event.target.checked;
    if (this.isLogOn == true) {
      this.enableSave = true;
    } else {
      this.enableSave = false;
    }
  }

  get perfval() {
    return this.personalFormGroup.controls;
  }

  get locfval(){
    return this.locationFormGroup.controls;
  }

  get emailfval(){
    return this.emailFormGroup.controls;
  }

  get loftfval(){
    return this.loftInfoFormGroup.controls;
  }



  passwordClick() {
    this.showNewPassword = !this.showNewPassword;
    setTimeout(() => {
      this.showNewPassword = !this.showNewPassword;
    }, 3000);
  }

  confirmClick() {
    this.showConfirmPassword = !this.showConfirmPassword;
    setTimeout(() => {
      this.showConfirmPassword = !this.showConfirmPassword;
    }, 3000);
  }

  registerLocationFilter() {
    const locationCtrl = this.locationFormGroup.get('locationCtrl');
    this.filteredLocations = locationCtrl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterLocations(value))
    );
  }

  private _filterLocations(value: string): LocationDTO[] {
    const filterValue = value.toLowerCase();
    return this.locations.filter(
      (location) => location.locationName?.toLowerCase().includes(filterValue) || location.city?.toLowerCase().includes(filterValue) || location.state?.toLowerCase().includes(filterValue) || location.clientName?.toLowerCase().includes(filterValue)
    );
  }

  verifyEmail(authInfo: any) {
    this.loadingUserDataByEmail = true;
    try {
      this.userFacadeService.verifyEmailFirebase(authInfo.authId).subscribe({
        next: (response) => {
          this.submitOnboardingDetails();
        }, error: (error) => {
          // handle error here ...
        }
      })
    } catch (error) {
      // handle error here
    }
  }

  onLocationSelected(event: MatAutocompleteSelectedEvent) {
    const selectedLocation = this.locations.find(location => location.locationName + "," + location.city + "," + location.state + "," + location.clientName == event.option.viewValue);
    //const selectedLocation = this.locations.find(location => location.locationName+","+","+",");
    if (selectedLocation) {
      this.locationFormGroup.get('locationIdCtrl').setValue(selectedLocation.id);
    }
  }

  submitOnboardingDetails() {
    this.loadingUserDataByEmail = true;
    try {
      const authId = (this.onBoardingUserDetails.userTypeId == 1) ? (this.onBoardingUserDetails.authId) : (this.signupAuthInfo.authId);
      const signInMethod = (this.onBoardingUserDetails.userTypeId == 1) ? (this.onBoardingUserDetails.signInMethod) : (this.signupAuthInfo.signInMethod);
      const dialCode = '+' + this.phoneInputComponent.selectedCountry.dialCode;
      const isoCode = this.phoneInputComponent.selectedCountry.iso2.toString().toUpperCase();
      const phoneNo = this.phoneInputComponent.phoneNumber;
      let data = {
        "id": 0,
        "userId": 0,
        "locationId": this.formSubmissionInfo.location.locationIdCtrl,
        "about": "",
        "studioName": this.formSubmissionInfo.loft.loftNameCtrl,
        "studioNumber": this.formSubmissionInfo.loft.loftNoCtrl,
        "studioImage": "",
        "emailEnabled": true,
        "smsEnabled": true,
        "onBoardingStylist": true,
        "personalInfo": {
          "authId": authId,
          "signInMethod": signInMethod,
          "firstName": this.formSubmissionInfo.personal.fnameCtrl,
          "lastName": this.formSubmissionInfo.personal.lnameCtrl,
          "email": this.formSubmissionInfo.email.emailCtrl,
          "countryCode": isoCode,
          "stdCode": dialCode,
          "phoneNumber": phoneNo,
          "smsNumber": this.formSubmissionInfo.personal.mobileCtrl,
          "images": "",
          "userRoleId": 0,
          "userTypeId": 3,
          "recordStatus": true,
          "registerStatus": true,
          "createdBy": 0
        }
      }
      this.stylistFacadeService.onboardStylist(data).subscribe({
        next: (response) => {
          if(response == "Studio number is already exists"){
            this.snackBar.errorSnackbar('Suite number was already in use');
            this.isProcessing = false;
            this.loadingUserDataByEmail = false;
            this.successDisplay = false;
            this.passwordMessage = "Suite number was already in use";
          }else if(response == "Already Exists"){
            this.isProcessing = false;
            this.loadingUserDataByEmail = false;
            this.successDisplay = false;
            this.snackBar.errorSnackbar('This email was already registered');
            this.passwordMessage = "This email was already registered";
          }else{
            this.completeRegistration = true;
            this.isProcessing = false;
            this.successDisplay = true;
            this.loadingUserDataByEmail = false;
            try {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              const scrollableElements = document.querySelectorAll('.login-form-part, .body-container, .main-content');
              scrollableElements.forEach((el: any) => {
                if (el && el.scrollTop) el.scrollTop = 0;
              });
            } catch (e) {}
           // window.location.href = 'https://serve.myvisitmaker.com/#/auth/login';
          }
        }, error: (error) => {
        }
      })
    } catch (error) {
      // handle error
    }
  }

  navigateUrl(){
    // window.top.location.href = 'https://serve.myvisitmaker.com/#/auth/login';
    this.emailFormGroup.reset();
    this.previewFormGroup.reset();
    this.personalFormGroup.reset();
    this.loftInfoFormGroup.reset();
    this.locationFormGroup.reset();
    this.successDisplay = false;
    this.completeRegistration = false;
    this.isProcessing = true;
    setTimeout(() => {
      this.isProcessing = false;
    }, 1000);
  }


  terms() {
    window.open('https://visitmaker.com/terms.html', '_blank');
  }

  privacy() {
    window.open('https://visitmaker.com/privacy-policy.html', '_blank');
  }

  setPasswordValidations() {
    this.passwordCtrl.setValidators([Validators.required, Validators.minLength(8), Validators.pattern('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$')]);
    this.confirmPassCtrl.setValidators([Validators.required]);
    this.personalFormGroup.setValidators([MustMatch('passwordCtrl', 'confirmPassCtrl')]);
  }

  removePasswordValidations() {
    this.passwordCtrl.setValidators(null);
    this.confirmPassCtrl.setValidators(null);
    this.personalFormGroup.setValidators(null);
  }

  fetchUserDetails() {
    this.loadingUserDataByEmail = true;
    const _email = this.emailFormGroup.get('emailCtrl').value;
    this.userFacadeService.GetUserByEmailOnBoarding(_email).subscribe({
      next: (response: UserOnboarding) => {
        this.loadingUserDataByEmail = false;
        this.onBoardingUserDetails = response;
        
        if (response.userType == "Customer") {
          if(this.onBoardingUserDetails.isFirebaseRegistered == false){
            this.hidePasswords = true;
            //this.passwordMessage = "*Your email is registered with us as a customer. Please continue to add yourself to the business and use the same password or Google/Apple login after you have been approved by the business owner/administrator.";
            this.removePasswordValidations();
            this.stopRegistration = false;
          }else{
            this.hidePasswords = true;
            this.passwordMessage = "*Your email is registered with us as a customer. Please continue to add yourself to the business and use the same password or Google/Apple login after you have been approved by the business owner/administrator.";
            this.removePasswordValidations();
            this.stopRegistration = false;
          }
         }
        else if (response.userType == "ServiceProvider") {
          this.hidePasswords = true;
          this.removePasswordValidations();
          this.passwordMessage = "*You are already a registered user. Please contact support@visitmaker.com";
          this.stopRegistration = true;
        } else {
          this.hidePasswords = false;
          this.setPasswordValidations();
          this.stopRegistration = false;
          this.passwordMessage = "";
        }

      }, error: (error) => {
        // handle error.
      }
    })
  }

  emailEvent(event){
    if(event.target.value){
      this.fetchUserDetails();
    }
  }

  onCountryChange(event) {

    if (event.iso2 !== 'us' && event.iso2 !== 'in') {
      this.selectedCountry = {
        "name": "United States",
        "iso2": "us",
        "dialCode": "1",
        "priority": 0,
        "flagClass": "US",
      }
      this.phoneInputComponent.selectedCountry = this.selectedCountry;
    } else {
      this.selectedCountry = event; // Otherwise, keep the selected country
    }

  }
}
